import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import FadeIn from '../../components/ui/FadeIn';
import ReactDOM from 'react-dom';
import { buttonClass } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { AuthorityList } from '../Authority';
import { NODE_SWATCH, NodeEditor } from '../DocSection';
import type { NodeColor, NodePatch, PlanEdge, PlanMember, PlanNode } from '../types';

export type MenuTarget = { kind: 'node'; id: string } | { kind: 'edge'; id: string } | { kind: 'pane' };

export interface MenuRequest {
  target: MenuTarget;
  /** Where the menu anchors, in client coordinates. */
  at: { x: number; y: number };
  /** The flow point under the pointer: where "Add topic here" puts the topic. */
  flow: { x: number; y: number };
  /** The nodes selected when the menu opened: what "Group selection" gathers. */
  selection: string[];
  /** Focus goes back here when the menu closes without an action that moves it. */
  returnFocus: HTMLElement | null;
}

export interface ContextMenuActions {
  open: (id: string) => void;
  save: (id: string, patch: NodePatch) => Promise<boolean>;
  accept: (id: string) => Promise<boolean>;
  reject: (node: PlanNode) => Promise<void>;
  /** Marks a topic done, or reopens a done one. */
  toggleDone: (node: PlanNode) => Promise<boolean>;
  remove: (node: PlanNode) => Promise<void>;
  removeEdge: (id: string) => void;
  /** Opens the inline label editor at the arrow's midpoint. */
  labelEdge: (id: string) => void;
  /** Draws an arrow, through the same call a dragged arrow makes. */
  connect: (sourceId: string, targetId: string) => void;
  /** Sets or clears who holds a node, through the same save the side panel uses. */
  setAuthority: (nodeId: string, authorityId: string | null) => Promise<boolean>;
  addAt: (title: string, flow: { x: number; y: number }) => Promise<boolean>;
  /** A group at the point, at the default size; its panel opens once it lands. */
  addGroupAt: (title: string, flow: { x: number; y: number }) => Promise<boolean>;
  /** A group fitted around these nodes, which then move into it. */
  groupSelection: (title: string, ids: string[]) => Promise<boolean>;
  /** Out of its group, into the one around that, or to the top level. */
  leaveGroup: (node: PlanNode) => Promise<boolean>;
  fit: () => void;
  /** Present only for a manager on a writable plan with topics on it. */
  tidy?: () => void;
  /** Present only where Lock lives in this menu (under 760px, for a manager). */
  toggleLock?: () => void;
}

export interface ConnectOption {
  id: string;
  title: string;
  /** The section number in flow order, '3'. */
  number?: string;
}

interface ContextMenuProps {
  request: MenuRequest;
  node?: PlanNode;
  edge?: PlanEdge;
  /** The topics this node may take an arrow to. Present only where "Connect to" is offered. */
  connectOptions?: ConnectOption[];
  /** Manager on a writable plan: accept, reject, add rather than propose, set authority, make groups. */
  canEdit: boolean;
  /** May add or propose topics: not a viewer, and the plan is writable. */
  canContribute: boolean;
  canEditText: (node: PlanNode) => boolean;
  /** Color and removal on this node: a manager, or its authority. */
  canEditNode: (node: PlanNode) => boolean;
  /** A manager, or the authority on either end. */
  canRemoveEdge: (edge: PlanEdge) => boolean;
  members: PlanMember[];
  locked: boolean;
  actions: ContextMenuActions;
  onClose: (returnFocus: boolean) => void;
}

const COLORS: NodeColor[] = ['none', 'gold', 'sky', 'moss', 'plum', 'rust', 'slate'];
const COLOR_NAME: Record<NodeColor, string> = {
  none: 'No color',
  gold: 'Gold',
  sky: 'Sky',
  moss: 'Moss',
  plum: 'Plum',
  rust: 'Rust',
  slate: 'Slate',
};

const MOTION = 'transition-colors duration-150 ease-emphasized motion-reduce:transition-none';
// The house Menu's panel and item, so a context menu reads as the same object.
const PANEL = 'nokey fixed z-50 rounded-lg bg-surface-container-high p-1 shadow-overlay motion-safe:animate-menuIn';
const ITEM = `np-state flex w-full items-center rounded-md px-3 py-2 text-left text-sm text-on-surface focus:bg-surface-container-highest focus:outline-none disabled:opacity-40 ${MOTION}`;
const DANGER = `flex w-full items-center rounded-md bg-red-500/10 px-3 py-2 text-left text-sm font-medium text-red-300 hover:bg-red-500/20 focus:bg-red-500/20 focus:outline-none ${MOTION}`;
const TONAL = buttonClass('tonal', 'sm');

interface Item {
  key: string;
  label: string;
  danger?: boolean;
  run: () => void;
}

const AddForm: React.FC<{
  propose: boolean;
  /** A group is named here and submitted under the menu item's own verb. */
  group?: { submit: string };
  onAdd: (title: string) => Promise<boolean>;
  onCancel: () => void;
}> = ({ propose, group, onAdd, onCancel }) => {
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const clean = title.trim();
    if (!clean || busy) return;
    setBusy(true);
    const ok = await onAdd(clean);
    if (!ok) setBusy(false);
  };
  return (
    <div className="space-y-2 p-2">
      <Field
        autoFocus
        value={title}
        maxLength={200}
        disabled={busy}
        aria-label={group ? 'Name of the new group' : propose ? 'Title of the proposed topic' : 'Title of the new topic'}
        placeholder={group ? 'Group name' : 'Topic title'}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          }
        }}
      />
      <div className="flex items-center gap-1">
        <button type="button" onClick={submit} disabled={busy || !title.trim()} className={TONAL}>
          {group ? group.submit : propose ? 'Propose' : 'Add'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={buttonClass('quiet', 'sm')}>
          Cancel
        </button>
      </div>
    </div>
  );
};

/** Type to narrow, arrows to move, Enter to draw the arrow. */
const ConnectPicker: React.FC<{
  from: PlanNode;
  options: ConnectOption[];
  onPick: (id: string) => void;
  onCancel: () => void;
}> = ({ from, options, onPick, onCancel }) => {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listId = `np-connect-${from.id}`;
  const q = query.trim().toLowerCase();
  const shown = q ? options.filter((o) => `${o.number ?? ''} ${o.title}`.toLowerCase().includes(q)) : options;
  const at = Math.min(active, Math.max(0, shown.length - 1));
  const optionId = (id: string) => `${listId}-${id}`;

  useEffect(() => {
    const el = shown[at] ? document.getElementById(optionId(shown[at].id)) : null;
    el?.scrollIntoView?.({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [at, query]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      if (shown.length === 0) return;
      setActive((at + (e.key === 'ArrowDown' ? 1 : shown.length - 1)) % shown.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (shown[at]) onPick(shown[at].id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    }
  };

  return (
    <div className="p-2">
      <Field
        autoFocus
        value={query}
        role="combobox"
        aria-expanded
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={shown[at] ? optionId(shown[at].id) : undefined}
        aria-label={`Connect ${from.title} to`}
        placeholder="Find a topic"
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
        }}
        onKeyDown={onKeyDown}
      />
      {shown.length === 0 ? (
        <p className="px-2 pb-1 pt-3 text-[13px] text-on-surface-variant">
          {options.length === 0 ? 'No other topic to connect to.' : 'No topic matches.'}
        </p>
      ) : (
        <ul id={listId} role="listbox" aria-label="Topics" className="mt-2 max-h-60 space-y-0.5 overflow-y-auto">
          {shown.map((o, i) => (
            <li
              key={o.id}
              id={optionId(o.id)}
              role="option"
              aria-selected={i === at}
              title={o.title}
              // Focus stays in the field, so the arrow keys and typing keep working after a click.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(o.id)}
              className={`np-state flex min-h-[32px] cursor-pointer items-center gap-1.5 rounded-md px-2 text-[13px] ${
                i === at ? 'bg-surface-container-highest text-on-surface' : 'text-on-surface-variant'
              } ${MOTION}`}
            >
              {o.number && <span className="flex-shrink-0 font-mono text-[11.5px] tabular-nums">{o.number}</span>}
              <span className="min-w-0 truncate">{o.title}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * The canvas's right-click menu, for a node, an arrow or empty space. Anchored at
 * the pointer and kept on screen; one open at a time (the canvas holds one request).
 * Closes on an outside press, Escape, scroll, zoom, resize, or once an item runs.
 */
// The panel's own menuIn animation covers the first mode; only later swaps fade.
const ModeSwap: React.FC<{ mode: string; first: string; children: React.ReactNode }> = ({ mode, first, children }) =>
  mode === first ? <div key={mode}>{children}</div> : <FadeIn key={mode}>{children}</FadeIn>;

const ContextMenu: React.FC<ContextMenuProps> = ({
  request,
  node,
  edge,
  connectOptions,
  canEdit,
  canContribute,
  canEditText,
  canEditNode,
  canRemoveEdge,
  members,
  locked,
  actions,
  onClose,
}) => {
  const panel = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<'list' | 'edit' | 'add' | 'add-group' | 'group' | 'connect' | 'authority'>('list');
  const firstMode = useRef(mode);
  const [pos, setPos] = useState(request.at);
  const { target } = request;

  // Keep the whole panel on screen: flip left or up from the pointer when it would overflow.
  useLayoutEffect(() => {
    const el = panel.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = request.at.x;
    let y = request.at.y;
    if (x + w > vw - 8) x = Math.max(8, x - w);
    if (y + h > vh - 8) y = Math.max(8, vh - h - 8);
    setPos({ x, y });
  }, [request.at.x, request.at.y, mode]);

  // Focus the first item on open, as the house Menu does, so the arrow keys work at once.
  useEffect(() => {
    if (mode === 'list') panel.current?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus();
  }, [mode]);

  useEffect(() => {
    const inside = (t: EventTarget | null) => !!panel.current && panel.current.contains(t as Node);
    const onDown = (e: PointerEvent) => {
      if (!inside(e.target)) onClose(false);
    };
    const onScroll = (e: Event) => {
      if (!inside(e.target)) onClose(false);
    };
    const onWheel = (e: WheelEvent) => {
      if (!inside(e.target)) onClose(false);
    };
    const onResize = () => onClose(false);
    document.addEventListener('pointerdown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('wheel', onWheel, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('wheel', onWheel, true);
      window.removeEventListener('resize', onResize);
    };
  }, [onClose]);

  // Focus goes home first; an action that moves it (Open, a confirm dialog) then takes it.
  const act = (fn: () => void) => () => {
    onClose(true);
    fn();
  };

  const items: Item[] = [];
  const groupable = canEdit && request.selection.length > 0;
  const edgeRemovable = !!edge && canRemoveEdge(edge);
  if (target.kind === 'edge' && edge && edgeRemovable) {
    items.push({
      key: 'label-edge',
      label: 'Label arrow…',
      // Not act(): its focus return would land after the editor's field takes focus, and steal it.
      run: () => {
        onClose(false);
        actions.labelEdge(edge.id);
      },
    });
    items.push({ key: 'remove-edge', label: 'Remove arrow', danger: true, run: act(() => actions.removeEdge(edge.id)) });
  } else if (target.kind === 'node' && node) {
    const proposed = node.status === 'proposed';
    items.push({ key: 'open', label: 'Open', run: act(() => actions.open(node.id)) });
    if (canEditText(node)) items.push({ key: 'edit', label: 'Edit', run: () => setMode('edit') });
    if (connectOptions) items.push({ key: 'connect', label: 'Connect to…', run: () => setMode('connect') });
    if (canEdit) {
      items.push({
        key: 'authority',
        label: node.authority_id ? 'Change authority…' : 'Set authority…',
        run: () => setMode('authority'),
      });
    }
    if (groupable) items.push({ key: 'group', label: 'Group selection', run: () => setMode('group') });
    if (node.parent_id && canEditNode(node)) {
      items.push({ key: 'leave-group', label: 'Remove from group', run: act(() => actions.leaveGroup(node)) });
    }
    if (proposed && canEdit) {
      items.push({ key: 'accept', label: 'Accept', run: act(() => actions.accept(node.id)) });
      items.push({ key: 'reject', label: 'Reject', danger: true, run: act(() => actions.reject(node)) });
    }
    if (!proposed && node.kind === 'topic' && canEditNode(node)) {
      items.push({ key: 'done', label: node.done_at ? 'Reopen' : 'Mark as done', run: act(() => actions.toggleDone(node)) });
    }
    if (!proposed && canEditNode(node)) {
      const label = node.kind === 'group' ? 'Remove group' : 'Remove topic';
      items.push({ key: 'remove', label, danger: true, run: act(() => actions.remove(node)) });
    }
  } else {
    // Empty space, and an arrow for someone who cannot change arrows.
    if (canContribute) {
      items.push({ key: 'add', label: canEdit ? 'Add topic here' : 'Propose topic here', run: () => setMode('add') });
    }
    if (canEdit) items.push({ key: 'add-group', label: 'Add group here', run: () => setMode('add-group') });
    if (groupable) items.push({ key: 'group', label: 'Group selection', run: () => setMode('group') });
    if (actions.tidy) items.push({ key: 'tidy', label: 'Tidy up', run: act(actions.tidy) });
    items.push({ key: 'fit', label: 'Fit view', run: act(actions.fit) });
    if (actions.toggleLock) {
      items.push({ key: 'lock', label: locked ? 'Unlock this plan' : 'Lock this plan', run: act(actions.toggleLock) });
    }
  }
  const showColors = target.kind === 'node' && !!node && canEditNode(node);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (mode === 'list') onClose(true);
      else setMode('list');
      return;
    }
    // The edit and add forms keep their arrow keys for the text fields.
    if (mode !== 'list') return;
    const list = Array.from(panel.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([disabled])') ?? []);
    const at = list.indexOf(document.activeElement as HTMLElement);
    let next = -1;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (at + 1) % list.length;
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (at - 1 + list.length) % list.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = list.length - 1;
    else if (e.key === 'Tab') {
      e.preventDefault();
      onClose(true);
      return;
    }
    if (next >= 0 && list.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      list[next].focus();
    }
  };

  const label =
    target.kind === 'node' && node ? `Actions for ${node.title}` : target.kind === 'edge' && edgeRemovable ? 'Arrow actions' : 'Canvas actions';

  const menu = (
    <div
      ref={panel}
      role={mode === 'list' ? 'menu' : 'dialog'}
      aria-label={label}
      className={`${PANEL} ${mode === 'list' ? 'min-w-[12rem]' : 'w-[min(20rem,calc(100vw-16px))]'}`}
      style={{ left: pos.x, top: pos.y }}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => e.preventDefault()}
    >
      <ModeSwap mode={mode} first={firstMode.current}>
        {mode === 'edit' && node && (
          <div className="p-2">
            <NodeEditor
              node={node}
              onCancel={() => setMode('list')}
              onSave={async (patch) => {
                const ok = await actions.save(node.id, patch);
                if (ok) onClose(true);
                return ok;
              }}
            />
          </div>
        )}
        {mode === 'add' && (
          <AddForm
            propose={!canEdit}
            onCancel={() => setMode('list')}
            onAdd={async (title) => {
              const ok = await actions.addAt(title, request.flow);
              if (ok) onClose(true);
              return ok;
            }}
          />
        )}
        {mode === 'add-group' && (
          <AddForm
            propose={false}
            group={{ submit: 'Add group' }}
            onCancel={() => setMode('list')}
            onAdd={async (title) => {
              const ok = await actions.addGroupAt(title, request.flow);
              // Not onClose(true): the new group's panel takes focus.
              if (ok) onClose(false);
              return ok;
            }}
          />
        )}
        {mode === 'group' && (
          <AddForm
            propose={false}
            group={{ submit: 'Group selection' }}
            onCancel={() => setMode('list')}
            onAdd={async (title) => {
              const ok = await actions.groupSelection(title, request.selection);
              if (ok) onClose(true);
              return ok;
            }}
          />
        )}
        {mode === 'connect' && node && connectOptions && (
          <ConnectPicker
            from={node}
            options={connectOptions}
            onCancel={() => setMode('list')}
            onPick={(targetId) => act(() => actions.connect(node.id, targetId))()}
          />
        )}
        {mode === 'authority' && node && (
          <AuthorityList
            nodeTitle={node.title}
            members={members}
            currentId={node.authority_id}
            onCancel={() => setMode('list')}
            onPick={(userId) =>
              act(() => {
                if (userId !== node.authority_id) actions.setAuthority(node.id, userId);
              })()
            }
          />
        )}
        {mode === 'list' && (
          <>
            {items
              .filter((i) => !i.danger)
              .map((i) => (
                <button key={i.key} type="button" role="menuitem" tabIndex={-1} onClick={i.run} className={ITEM}>
                  {i.label}
                </button>
              ))}
            {showColors && node && (
              <div role="group" aria-label="Color" className="flex flex-wrap items-center gap-0.5 px-1 py-1">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    role="menuitemradio"
                    tabIndex={-1}
                    aria-checked={node.color === c}
                    aria-label={COLOR_NAME[c]}
                    onClick={act(() => {
                      if (c !== node.color) actions.save(node.id, { color: c });
                    })}
                    className={`np-state flex h-8 w-8 items-center justify-center rounded-full focus:bg-surface-container-highest focus:outline-none ${MOTION}`}
                  >
                    <span
                      aria-hidden="true"
                      className={`block h-3 w-5 rounded-full ${c === 'none' ? 'bg-surface-container-highest' : ''} ${
                        node.color === c ? 'ring-2 ring-on-surface ring-offset-2 ring-offset-surface-container-high' : ''
                      }`}
                      style={c === 'none' ? undefined : { backgroundColor: NODE_SWATCH[c] }}
                    />
                  </button>
                ))}
              </div>
            )}
            {items.some((i) => i.danger) && items.some((i) => !i.danger) && <div role="separator" className="mx-2 my-1 h-px bg-outline-variant" />}
            {items
              .filter((i) => i.danger)
              .map((i) => (
                <button key={i.key} type="button" role="menuitem" tabIndex={-1} onClick={i.run} className={DANGER}>
                  {i.label}
                </button>
              ))}
          </>
        )}
      </ModeSwap>
    </div>
  );

  // A portal, so no transformed or clipped ancestor can move or cut the menu.
  return ReactDOM.createPortal(menu, document.body);
};

export default ContextMenu;
